'use client';

import { useState, useEffect, useCallback } from 'react';
// import { TrendingUp, TrendingDown } from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { get } from '@/utils/service';

type TimePeriod = 'daily' | 'weekly' | 'monthly';

interface DataPoint {
  date: string;
  value: number;
  currency: string;
}

interface RevenueChartData {
  total_value: number;
  latest_value: number;
  previous_value: number;
  percentage_change: number;
  high_value: number;
  low_value: number;
  average_value: number;
  currency: string;
  period: string;
  data_points: DataPoint[];
}

interface RevenueChartResponse {
  success: boolean;
  data: {
    revenue_chart: RevenueChartData;
  };
}

const formatCurrency = (amount: number, currency: string = "USD") => {
  return `${currency} ${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

const formatCurrencyShort = (amount: number, currency: string = "USD") => {
  if (amount >= 1000000) {
    return `${currency} ${(amount / 1000000).toFixed(1)}M`;
  }
  if (amount >= 1000) {
    return `${currency} ${(amount / 1000).toFixed(0)}k`;
  }
  return `${currency} ${amount.toFixed(0)}`;
};

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: DataPoint;
  }>;
  currency?: string;
}

const CustomTooltip = ({ active, payload, currency = "USD" }: TooltipProps) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-2 shadow-lg">
        <div className="text-xs text-gray-500 mb-1">{data.date}</div>
        <div className="flex items-center gap-2">
          <div className="text-xs font-bold text-gray-900">{formatCurrency(data.value, data.currency || currency)}</div>
        </div>
      </div>
    );
  }
  return null;
};

const LoadingSkeleton = () => (
  <div className="bg-white rounded-xl p-4">
    {/* Header Skeleton */}
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 gap-3">
      <div className="w-full sm:w-auto">
        <div className="h-3 w-24 bg-gray-200 rounded mb-2 animate-pulse"></div>
        <div className="flex items-baseline gap-2">
          <div className="h-6 w-32 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>
      
      {/* Tabs Skeleton */}
      <div className="flex bg-gray-100 rounded-lg p-0.5 w-full sm:w-auto">
        {[1, 2, 3].map((i) => (
          <div key={i} className="px-3 py-1 w-20 bg-gray-200 rounded-md animate-pulse"></div>
        ))}
      </div>
    </div>

    {/* Stats Row Skeleton */}
    <div className="flex flex-wrap items-center justify-between gap-2 text-xs mb-3">
      <div className="flex flex-wrap items-center gap-3">
        {[1, 2].map((i) => (
          <div key={i} className="flex items-center gap-1">
            <div className="h-3 w-12 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-3 w-16 bg-gray-200 rounded animate-pulse"></div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {[1, 2].map((i) => (
          <div key={i} className="flex items-center gap-1">
            <div className="h-3 w-8 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-3 w-16 bg-gray-200 rounded animate-pulse"></div>
          </div>
        ))}
      </div>
    </div>

    {/* Chart Area Skeleton */}
    <div className="h-56 w-full relative">
      <div className="absolute inset-0 flex flex-col">
        {/* Y-axis labels */}
        <div className="flex justify-between items-start h-full">
          <div className="flex flex-col justify-between h-full">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-2 w-12 bg-gray-200 rounded animate-pulse"></div>
            ))}
          </div>
          <div className="flex-1 ml-2">
            {/* Chart line skeleton */}
            <div className="relative h-full w-full">
              <div className="absolute top-1/4 left-0 right-0 h-px bg-gray-100"></div>
              <div className="absolute top-1/2 left-0 right-0 h-px bg-gray-100"></div>
              <div className="absolute top-3/4 left-0 right-0 h-px bg-gray-100"></div>
              
              {/* Animated wave line */}
              <div className="absolute inset-0">
                <div className="h-1 bg-gradient-to-r from-transparent via-gray-200 to-transparent animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
        
        {/* X-axis labels */}
        <div className="flex justify-between mt-2">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-2 w-8 bg-gray-200 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default function RevenueChart() {
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('monthly');
  const [chartData, setChartData] = useState<RevenueChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fetchRevenueData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await get<RevenueChartResponse>(`/dashboard/revenue-chart?period=${timePeriod}`);
      if (response.success && response.data?.revenue_chart) {
        setChartData(response.data.revenue_chart);
      }
    } catch (error) {
      console.error('Error fetching revenue chart data:', error);
    } finally {
      setLoading(false);
    }
  }, [timePeriod]);

  useEffect(() => {
    fetchRevenueData();
  }, [fetchRevenueData]);

  if (loading) {
    return <LoadingSkeleton />;
  }

  const data = chartData?.data_points || [];
  const totalValue = chartData?.total_value || 0;
  // const latestValue = chartData?.latest_value || 0;
  // const percentageChange = chartData?.percentage_change || 0;
  // const highValue = chartData?.high_value || 0;
  // const lowValue = chartData?.low_value || 0;
  // const averageValue = chartData?.average_value || 0;
  const currency = chartData?.currency || "USD";
  // const isPositive = percentageChange >= 0;

  return (
    <div className="bg-primary/5 border border-primary/50 rounded-xl p-3 sm:p-4 ">
      {/* Header - Responsive layout */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 gap-3">
        <div className="w-full sm:w-auto">
          <p className="text-xs text-primary/80 font-medium mb-1">Revenue Analytics</p>
          <div className="flex flex-col xs:flex-row xs:items-baseline gap-1 xs:gap-2">
            <span className="text-lg sm:text-xl font-bold text-primary break-all">
              {formatCurrency(totalValue, currency)}
            </span>
            {/* <div className={`flex items-center gap-1 ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
              {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              <span className="text-xs font-medium">
                {isPositive ? '+' : ''}{Math.abs(percentageChange).toFixed(1)}%
              </span>
            </div> */}
          </div>
        </div>

        {/* Time Period Tabs - Mobile optimized */}
        <div className="flex bg-gray-100 rounded-lg p-0.5 w-full sm:w-auto">
          {(['daily', 'weekly', 'monthly'] as TimePeriod[]).map((period) => (
            <button
              key={period}
              onClick={() => setTimePeriod(period)}
              className={`px-2 sm:px-3 py-1 text-xs font-medium rounded-md transition-all flex-1 sm:flex-none ${
                timePeriod === period
                  ? 'bg-primary text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {isMobile ? period.charAt(0).toUpperCase() : period.charAt(0).toUpperCase() + period.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Row - Responsive layout */}
      <div className="flex flex-col xs:flex-row items-start xs:items-center justify-between flex-wrap gap-2 text-xs mb-3">
        {/* <div className="flex flex-wrap items-center gap-2 xs:gap-4">
          <div className="flex items-center gap-1">
            <span className="text-gray-500 whitespace-nowrap">Latest:</span>
            <span className="font-semibold text-gray-900 whitespace-nowrap">
              {formatCurrency(latestValue, currency)}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-gray-500 whitespace-nowrap">Average:</span>
            <span className="font-semibold text-gray-900 whitespace-nowrap">
              {formatCurrency(averageValue, currency)}
            </span>
          </div>
        </div> */}
        {/* <div className="flex flex-wrap items-center gap-2 xs:gap-4 text-gray-500">
          <span className="whitespace-nowrap">
            High: <span className="text-green-600 font-medium whitespace-nowrap">
              {formatCurrency(highValue, currency)}
            </span>
          </span>
          <span className="whitespace-nowrap">
            Low: <span className="text-red-600 font-medium whitespace-nowrap">
              {formatCurrency(lowValue, currency)}
            </span>
          </span>
        </div> */}
      </div>

      {/* Chart - Responsive container */}
      <div className="h-48 sm:h-56 w-full">
        {data.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data}
              margin={{ 
                top: 10, 
                right: isMobile ? 5 : 10, 
                left: isMobile ? -10 : 0, 
                bottom: 0 
              }}
            >
              <defs>
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f97316" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#f97316" stopOpacity={0.05} />
                </linearGradient>
              </defs>

              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#e5e7eb"
                vertical={false}
              />

              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ 
                  fontSize: isMobile ? 8 : 10, 
                  fill: '#6b7280' 
                }}
                tickMargin={isMobile ? 4 : 8}
                interval={isMobile ? 'preserveStartEnd' : 0}
                minTickGap={isMobile ? 10 : 0}
              />

              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ 
                  fontSize: isMobile ? 8 : 10, 
                  fill: '#6b7280' 
                }}
                tickFormatter={(value) => formatCurrencyShort(value, currency)}
                tickMargin={isMobile ? 4 : 8}
                width={isMobile ? 40 : 55}
              />

              <Tooltip 
                content={<CustomTooltip currency={currency} />}
                wrapperStyle={{ zIndex: 1000 }}
              />

              <Area
                type="monotone"
                dataKey="value"
                stroke="#f97316"
                strokeWidth={isMobile ? 1.5 : 2}
                fill="url(#colorValue)"
                dot={false}
                activeDot={{
                  r: isMobile ? 3 : 4,
                  fill: '#f97316',
                  stroke: '#fff',
                  strokeWidth: isMobile ? 1 : 2,
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-full text-xs text-gray-500">
            No data available
          </div>
        )}
      </div>
    </div>
  );
}